import { Component, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { CookieBannerHelperService } from '@client/app/modules/cookie-banner/services/helper/helper.service';
import { FormDomainScriptGuideService } from '@client/app/forms/domain-script-guide/form-domain-script-guide.service';
import { MonDialog } from '@client/app/modules/dialog-upgrade-wrapper/mon-dialog.service';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { MonModalService } from 'app/modules/modals/services/mon-modal/mon-modal.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { safeClone } from '@client/app/shared/utils/object-utils';
import {
    ConsentManagerComponent,
} from '@client/app/forms/domain-script-guide/features/consent-manager/consent-manager.component';

@Component({
    selector: 'form-domain-script-guide-features-cookie-banner',
    standalone: false,
    templateUrl: './domain-script-guide.component.html',
})
export class FormDomainScriptGuideFeaturesCookieBannerComponent implements OnInit, OnDestroy, OnChanges {
    @Input() domain: any;
    @Input() cookieBannerModel: any;

    settingEnabled: boolean = false;
    dialogId: any = '';
    listenerIds: number[] = [];
    private cmSettingsFormIsValid: boolean = true;

    constructor (
        private cookieBannerHelperService: CookieBannerHelperService,
        private formDomainScriptGuideService: FormDomainScriptGuideService,
        private monDialog: MonDialog,
        private monEventsService: MonEventService,
        private monModalService: MonModalService,
        private translateService: TranslateService,
    ) {}

    ngOnInit () {
        this.activate();
    }

    ngOnDestroy () {
        this.onDestroy();
    }

    ngOnChanges () {
        this.onChanges();
    }

    private activate () {
        const firstTimeEnableCM = !(this.cookieBannerModel?.settings?.enabled);
        this.cookieBannerModel.setSettings({
            firstTimeEnableCM: firstTimeEnableCM,
        });

        const updateConsentManagerSettingsEventId =
            this.monEventsService.addListener('updateConsentManagerSettings' as any, (consentManagerSettings: any) => {
                this.cookieBannerModel = Object.assign(this.cookieBannerModel, {
                    settings: safeClone(consentManagerSettings),
                });
            });
        this.listenerIds.push(updateConsentManagerSettingsEventId);

        const closeConsentManagerDialogEventId = this.monEventsService.addListener('closeConsentManagerDialog' as any, () => {
            if (Array.isArray(this.dialogId)) {
                this.monDialog.close(this.dialogId[0], null);
            } else {
                this.monDialog.close(this.dialogId, null);
            }
        });
        this.listenerIds.push(closeConsentManagerDialogEventId);

        const cmSettingsFormValidStatusEventId =
            this.monEventsService.addListener('cmSettingsFormValidStatus' as any, (valid: boolean) => {
                this.cmSettingsFormIsValid = valid;
            });

        this.listenerIds.push(cmSettingsFormValidStatusEventId);
    }

    private onChanges () {
        this.settingEnabled = this.cookieBannerModel?.settings?.enabled || false;
    }

    onEnabledChange (value) {
        this.monEventsService.run('FORM_UNSAVED_UPDATE' as any);
        this.settingEnabled = value;
        setTimeout(() => {
            this.cookieBannerModel.setSettings({ enabled: this.settingEnabled });
            this.updateDomainSettings();
        });
    }

    async openConsentManagerScriptSettings () {
        const params = {
            body: ConsentManagerComponent,
            size: 'md',
            data: {
                settings: this.cookieBannerModel.settings,
            },
        };
        this.formDomainScriptGuideService.setShowScript(false);

        const callback = (settings: any) => {
            if (settings) {
                this.monEventsService.run('FORM_UNSAVED_UPDATE' as any);
                this.cookieBannerModel.settings = this.formDomainScriptGuideService.getDomainSettings().cookie_banner;
            }
            this.formDomainScriptGuideService.setShowScript(true);
        };
        const onBeforeClose = () => new Promise((resolve) => {
            if (this.cmSettingsFormIsValid) {
                resolve(true);
            } else {
                const discardChangesConfirmationText = this.translateService.getString(
                    'The settings form contains incorrect data. Close the form anyway?',
                );
                this.monModalService.confirm(discardChangesConfirmationText).then(() => {
                    resolve(true);
                }, () => {
                    resolve(false);
                });
            }
        });

        this.dialogId = await this.monEventsService.run(MON_EVENTS.LOAD_DIALOG, { params, callback, onBeforeClose });
    }

    canUseFeature () {
        return this.cookieBannerHelperService.isAbleToSetupScript(this.domain);
    }

    private onDestroy () {
        for (const listenerId of this.listenerIds) {
            this.monEventsService.remove(listenerId);
        }
    }

    private updateDomainSettings () {
        setTimeout(() => {
            this.formDomainScriptGuideService.setCookieBannerEdit(this.cookieBannerModel);
        });
    }
}
